import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { CloudUpload, CheckCircle2, AlertTriangle, Loader2, Clock } from "lucide-react";
import { api } from "../lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function fmtSize(bytes) {
  if (!bytes) return null;
  const mb = bytes / 1024 / 1024;
  if (mb < 1) return `${Math.round(bytes / 1024)} KB`;
  return `${mb.toFixed(1)} MB`;
}

function fmtBig(bytes) {
  if (bytes == null) return "—";
  const gb = bytes / 1024 / 1024 / 1024;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
  const mb = bytes / 1024 / 1024;
  return `${mb.toFixed(0)} MB`;
}

export default function BackupStatusCard() {
  const [st, setSt] = useState(null);
  const [space, setSpace] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/backup/status");
      setSt(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const loadSpace = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/backup/space");
      if (data && data.connected && data.allocated_bytes) setSpace(data);
      else setSpace(null);
    } catch {
      setSpace(null);
    }
  }, []);

  useEffect(() => {
    load();
    loadSpace();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load, loadSpace]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const data = await load();
      if (data && data.status !== "running") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setBusy(false);
        if (data.status === "ok") { toast.success("Backup completato su Dropbox"); loadSpace(); }
        else if (data.status === "error") toast.error("Backup fallito: " + (data.error || ""));
      }
    }, 4000);
  }, [load, loadSpace]);

  const runNow = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/admin/backup/run-now");
      toast.message("Backup avviato…", { description: "Sto creando lo ZIP e caricandolo su Dropbox." });
      setSt((s) => ({ ...(s || {}), status: "running" }));
      startPolling();
    } catch (e) {
      setBusy(false);
      const msg = e?.response?.data?.detail || "Errore nell'avvio del backup";
      toast.error(msg);
    }
  }, [startPolling]);

  if (!st) return null;

  const running = st.status === "running" || busy;
  const connected = st.dropbox_connected;

  let badge;
  if (running) {
    badge = { cls: "bg-amber-100 text-amber-800", icon: <Loader2 className="w-3.5 h-3.5 animate-spin" />, label: "In corso" };
  } else if (st.status === "ok") {
    badge = { cls: "bg-emerald-100 text-emerald-800", icon: <CheckCircle2 className="w-3.5 h-3.5" />, label: "OK" };
  } else if (st.status === "error") {
    badge = { cls: "bg-red-100 text-red-700", icon: <AlertTriangle className="w-3.5 h-3.5" />, label: "Errore" };
  } else {
    badge = { cls: "bg-slate-100 text-slate-600", icon: <Clock className="w-3.5 h-3.5" />, label: "Mai eseguito" };
  }

  const size = fmtSize(st.size_bytes);

  return (
    <div
      className="rounded-xl border border-ink/10 bg-white/70 backdrop-blur px-3.5 py-3 shadow-sm w-full sm:w-[320px]"
      data-testid="backup-status-card"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 text-ink">
          <CloudUpload className="w-4 h-4 text-slate1" />
          <span className="text-[11px] font-display uppercase tracking-[0.12em]">Backup Dropbox</span>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.cls}`}
          data-testid="backup-status-badge"
        >
          {badge.icon}
          {badge.label}
        </span>
      </div>

      <div className="text-[12px] text-ink/80 leading-tight" data-testid="backup-last-date">
        Ultimo backup: <span className="font-semibold">{fmtDate(st.last_success_at)}</span>
        {size ? <span className="text-slate1"> · {size}</span> : null}
      </div>
      {st.status === "error" && st.error ? (
        <div className="text-[11px] text-red-600 mt-1 line-clamp-2" data-testid="backup-error">{st.error}</div>
      ) : null}
      {!connected ? (
        <div className="text-[11px] text-amber-700 mt-1">Dropbox non collegato. Collegalo nella sezione Dropbox.</div>
      ) : null}

      {space && space.allocated_bytes ? (
        (() => {
          const pct = Math.min(100, Math.round((space.used_bytes / space.allocated_bytes) * 100));
          const nearFull = pct >= 90;
          return (
            <div className="mt-2" data-testid="backup-space">
              <div className="flex items-center justify-between text-[11px] text-ink/70 mb-1">
                <span>Spazio Dropbox</span>
                <span className="font-semibold text-ink/80">{pct}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-ink/10 overflow-hidden">
                <div
                  className={`h-full rounded-full ${nearFull ? "bg-red-500" : "bg-emerald-500"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="text-[10.5px] text-slate1 mt-1" data-testid="backup-space-label">
                {fmtBig(space.used_bytes)} usati · {fmtBig(space.free_bytes)} liberi / {fmtBig(space.allocated_bytes)} totali
              </div>
            </div>
          );
        })()
      ) : null}

      <button
        onClick={runNow}
        disabled={running || !connected}
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-ink text-cream text-[12px] font-semibold px-3 py-2 hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        data-testid="backup-run-now-btn"
      >
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5" />}
        {running ? "Backup in corso…" : "Esegui backup ora"}
      </button>
    </div>
  );
}
